import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollReveal, StaggerContainer, StaggerItem } from '@/components/motion'
import { BRAND } from './halftone'

/** Hover panel colours; the fourth is black in light mode and blue in dark. */
const CELL_COLORS = [BRAND.blue, BRAND.purple, BRAND.pink]

/**
 * Platform capability grid (in place of the Solutions page's rotated cards):
 * a hairline grid of cells, each with a line icon in its colour, title and copy.
 * On hover a brand-colour panel wipes up from the bottom (clip-path) and the
 * text turns white. Hover only applies on hover-capable pointers.
 */
export function CapabilityGrid({
  capabilities,
}: {
  capabilities: Array<{ icon: LucideIcon; title: string; description: string }>
}) {
  return (
    <StaggerContainer
      amount={0.1}
      className="grid border-l border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)] sm:grid-cols-2 xl:grid-cols-4"
    >
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        const color = CELL_COLORS[i % 4]
        return (
          <StaggerItem
            key={cap.title}
            className={cn(
              'group relative flex min-h-[20rem] flex-col justify-between gap-14 overflow-hidden border-b border-r border-[var(--color-landing-gray-200)] p-8 dark:border-[var(--color-landing-gray-800)] md:min-h-[24rem] md:p-10',
              !color && '[--cell:#0a0a0a] dark:[--cell:#4d44ff]'
            )}
            style={color ? ({ '--cell': color } as CSSProperties) : undefined}
          >
            <span
              aria-hidden
              className="absolute inset-0 bg-[var(--cell)] transition-[clip-path] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [clip-path:inset(100%_0_0_0)] group-hover:[clip-path:inset(0_0_0_0)]"
            />
            <Icon
              aria-hidden
              className="relative h-12 w-12 text-[var(--cell)] transition-colors duration-300 group-hover:text-white md:h-14 md:w-14"
              strokeWidth={1.25}
            />
            <div className="relative">
              <h3 className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] tracking-[-0.01em] text-[var(--color-landing-gray-900)] transition-colors duration-300 group-hover:text-white dark:text-white md:text-xl">
                {cap.title}
              </h3>
              <p className="landing-text-body-sm mt-4 text-[var(--color-landing-gray-600)] transition-colors duration-300 group-hover:text-white/90 dark:text-[var(--color-landing-gray-400)]">
                {cap.description}
              </p>
            </div>
          </StaggerItem>
        )
      })}
    </StaggerContainer>
  )
}

/** Four business-impact figures under the grid: stat, title, copy, hairline dividers. */
export function ImpactRow({
  benefits,
}: {
  benefits: Array<{ icon: LucideIcon; title: string; description: string; stat?: string }>
}) {
  return (
    <StaggerContainer
      amount={0.1}
      className="grid gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-[var(--color-landing-gray-200)] dark:lg:divide-[var(--color-landing-gray-800)]"
    >
      {benefits.map((b) => (
        <StaggerItem key={b.title} className="sm:pr-8 lg:px-8 lg:first:pl-0 lg:last:pr-0">
          {b.stat && (
            <p className="font-[family-name:var(--font-landing-display)] text-[clamp(1.75rem,2.6vw,2.75rem)] uppercase leading-[1.3] tracking-[-0.02em] text-[var(--color-landing-secondary)] dark:text-[#c94dff]">
              {b.stat}
            </p>
          )}
          <h4 className="mt-3 text-lg font-semibold text-[var(--color-landing-gray-900)] dark:text-white">{b.title}</h4>
          <p className="landing-text-body-sm mt-2 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
            {b.description}
          </p>
        </StaggerItem>
      ))}
    </StaggerContainer>
  )
}

/** Index of platform pages for the FAQ aside; the current page is marked. */
export function PlatformIndex({
  items,
  current,
}: {
  items: Array<{ slug: string; label: string }>
  current: string
}) {
  return (
    <ScrollReveal>
      <nav aria-labelledby="platform-index-heading">
        <h2
          id="platform-index-heading"
          className="font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4] text-[var(--color-landing-gray-900)] dark:text-white"
        >
          Explore the platform
        </h2>
        <ul className="mt-6 border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
          {items.map((item) => {
            const isCurrent = item.slug === current
            return (
              <li key={item.slug} className="border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
                <Link
                  to={`/platform/${item.slug}`}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={cn(
                    'group flex items-center justify-between gap-4 px-3 py-4 text-base font-semibold transition-colors duration-200',
                    isCurrent
                      ? 'bg-[var(--color-landing-secondary)] text-white'
                      : 'text-[var(--color-landing-gray-900)] hover:bg-[var(--color-landing-gray-50)] hover:text-[var(--color-landing-accent)] dark:text-white dark:hover:bg-[#12121a] dark:hover:text-[#4d44ff]'
                  )}
                >
                  {item.label}
                  <ArrowRight
                    aria-hidden
                    className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1"
                  />
                </Link>
              </li>
            )
          })}
        </ul>
      </nav>
    </ScrollReveal>
  )
}
